/**
 * @file UploadForm.js
 * @module src/js/components/UploadForm
 * @description
 * **A reusable upload form component for various uploads.**
 * - Renders a form for uploading items with date, title, and (optionally) file(s).
 * - Validates input fields and enables upload button accordingly.
 * - Supports both upload (POST) and update (PUT) operations.
 * - Use modal messages to indicate upload/update status.
 * - Redirects to login if uid is 'Guest'.
 * 
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add modal messages. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUpload hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import ChooseAFile from "./ChooseAFile";
import useUpload from "../hooks/useUpload";
import useUpdate from "../hooks/useUpdate";
import SpanARow from "./SpanARow";
import DateSelection from "./DateSelection";
import CanUploadCheck from "./CanUploadCheck";

export default function UploadForm({
    request,
    acceptList = [],
    setData,
    text = null,
    textarea = false,
    item = null,
    updatedID = -1,
    setUpdatedID,
    from,
    scrollTo,
}) {
    const navigate = useNavigate();
    const { uid } = useUser();
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [year, setYear] = useState('');
    const [month, setMonth] = useState('');
    const [day, setDay] = useState('');
    const [title, setTitle] = useState('');
    const [files, setFiles] = useState(new Array(acceptList.length).fill(null));
    const [canUpload, setCanUpload] = useState(false);
    const [uploading, setUploading] = useState(false);
    const upload = useUpload({
        setData,
        setUploading,
        setShowUploadForm,
    });
    const update = useUpdate({
        setData,
        setUpdating: setUploading,
        setUpdatedID,
        setShowUploadForm,
    });

    if (item != null && (typeof setUpdatedID !== "function" || updatedID === -1)) {
        throw new Error("setUpdatedID function and valid updatedID are required when editing an existing item.");
    }
    
    const body = useMemo(() => ({
        y: year,
        m: month,
        d: day,
        title,
        id: updatedID !== -1 ? updatedID : undefined,
    }), [year, month, day, title, updatedID]);

    const handleClickSaveUpload = useCallback(() => {
        const options = { request, body, files, from, scrollTo };
        if (item) {
            update(options);
        } else {
            upload(options);
        }
    }, [item, update, upload, request, body, files, from, scrollTo]);

    const handleClickCancel = useCallback(() => {
        if (item) {
            setUpdatedID(-1); // For PUT (update)
        } else {
            setShowUploadForm(false); // For POST (upload)
        }
    }, [item, setUpdatedID]);

    useEffect(() => {
        const today = new Date();
        if (item) {
            const [yearPart, monthPart, dayPart] = item.date.split('-');
            setYear(yearPart);
            setMonth(monthPart);
            setDay(dayPart);
        } else {
            setYear(String(today.getFullYear())); // Get year (YYYY)
            setMonth(String(today.getMonth() + 1).padStart(2, "0")); // Get month (MM)
            setDay(String(today.getDate()).padStart(2, "0")); // Get day (DD)
        }
        setTitle(item?.title || '');
        if (item) {
            setShowUploadForm(true); // For PUT (update)
        }
        setFiles(new Array(acceptList.length).fill(null));
    }, [item, showUploadForm, acceptList.length]);

    return uid === 'Guest' ? (
        <SpanARow
            colSpan="3"
            onClick={() => navigate(
                '/login',
                { state: { from, scrollTo } }
            )}
            text='🔑 Log in to upload, edit, or delete'
        />
    ) : (
        showUploadForm ? (
            <tr>
                {/* Date */}
                <DateSelection
                    item={item}
                    showUploadForm={showUploadForm}
                    year={year}
                    month={month}
                    day={day}
                    setYear={setYear}
                    setMonth={setMonth}
                    setDay={setDay}
                />

                {/* Title and File(s) */}
                <td>
                    {textarea ? (
                        <textarea
                            type="text" placeholder="Title (Markdown supported. E.g., **bold**, *italic*, [link](url))"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required style={{ height: '10em', fontSize: '0.8em', marginBottom: '5px' }} autoFocus
                        />
                    ) : (
                        <input type="text" placeholder="Title"
                            onChange={(e) => setTitle(e.target.value)}
                            value={title}
                            required style={{ marginBottom: '5px' }} autoFocus
                        />
                    )}
                    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        {acceptList.map((accept, i) => (
                            <ChooseAFile
                                key={i}
                                index={i}
                                accept={accept}
                                files={files}
                                setFiles={setFiles}
                            />
                        ))}
                    </div>
                    <CanUploadCheck
                        year={year}
                        month={month}
                        day={day}
                        hasDateField={true}
                        title={title}
                        files={files}
                        isAcceptListEmpty={acceptList.length === 0}
                        setCanUpload={setCanUpload}
                        editMode={!!item}
                    />
                </td>

                {/* Upload and Cancel Buttons */}
                <td className='hori-vert-center' width='10%'>
                    <button
                        type="submit"
                        style={{ marginBottom: '15px' }}
                        disabled={!canUpload || uploading || (
                            year === item?.date.split('-')[0] &&
                            month === item?.date.split('-')[1] &&
                            day === item?.date.split('-')[2] &&
                            title === item?.title &&
                            files.every(file => file === null)
                        )}
                        onClick={handleClickSaveUpload}
                    >
                        {item ? 'Save' : 'Upload'}
                    </button>
                    <button
                        onClick={handleClickCancel}
                        disabled={uploading}
                    >
                        Cancel
                    </button>
                </td>
            </tr>
        ) : (
            <SpanARow
                colSpan="3"
                onClick={() => setShowUploadForm(true)}
                text={text}
            />
        )
    );
}